import { useMemo } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { ExpenseInput, TrackedItemInput } from "../../types";
import type { ReceiptDraft, ReceiptSplitPlan } from "../../types/receipt";
import { receiptSplitExpense } from "../../lib/receipt-splits";
import { receiptLinePriceRepair } from "../../lib/receipt-price-reconciliation";

export function useReceiptInput(input: ExpenseInput, setInput: Dispatch<SetStateAction<ExpenseInput>>, setTrackedItems: Dispatch<SetStateAction<TrackedItemInput[]>>, receipt?: ReceiptDraft, onReceiptChange?: (value: ReceiptDraft) => void) {
  const result = useMemo(() => {
    if (!receipt) return { input, error: "" };
    try { return { input: receiptSplitExpense(receipt, input), error: "" }; }
    catch (error) { return { input, error: error instanceof Error ? error.message : "Check the receipt split." }; }
    // The receipt's expense is mirrored from this form; using it as a dependency would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, receipt?.items, receipt?.categorySplit]);
  function changePlan(plan: ReceiptSplitPlan, nextExpense?: ExpenseInput, repairLinePrices?: boolean) {
    if (!receipt || !onReceiptChange) return;
    if (nextExpense) setInput(nextExpense);
    let next: ReceiptDraft = { ...receipt, expense: nextExpense || result.input, categorySplit: plan };
    if (repairLinePrices) {
      const repaired = receiptLinePriceRepair(next);
      if (!repaired) return;
      next = repaired;
      setTrackedItems(current => current.map(item => {
        const corrected = next.items.find(row => row.id === item.id);
        return corrected ? { ...item, total: corrected.total } : item;
      }));
      setInput(next.expense);
    }
    onReceiptChange(next);
  }
  return { receiptInput: result.input, receiptError: result.error, changePlan };
}
